import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Grid, Alert, Switch } from '@mantine/core';
import { Form, useForm } from 'react-hook-form';
import { TextInput, NativeSelect } from 'react-hook-form-mantine';
import yup from '../../lib/yup';
import { useState } from 'react';
import { errorToast, successToast } from '../../lib/helpers';
import { modals } from '@mantine/modals';
import { XCircleIcon } from '@heroicons/react/24/outline';

const validation = yup.object().shape({
  username: yup
    .string()
    .required('Username is required')
    .min(3, 'Username must be at least 3 characters')
    .matches(/^[a-zA-Z0-9_.@-]+$/, 'Username can only contain letters, numbers, dots, underscores, @ and hyphens'),
  email: yup
    .string()
    .email('Invalid email format')
    .required('Email is required'),
  password: yup
    .string()
    .when('$isEdit', {
      is: false,
      then: (schema) => schema
        .required('Password is required')
        .min(8, 'Password must be at least 8 characters')
        .matches(
          /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]+$/,
          'Password must contain at least one letter, one number, and one special character'
        ),
      otherwise: (schema) => schema.notRequired()
    }),
  role: yup
    .string()
    .required('Role is required')
    .oneOf(['admin', 'bm', 'sales', 'rep', 'fleet'], 'Invalid role'),
  branch: yup
    .string()
    .required('Branch is required'),
  allow_login: yup
    .boolean()
    .required('Login access is required'),
  status: yup
    .string()
    .required('Status is required')
    .oneOf(['active', 'inactive', 'locked'], 'Invalid status')
});

const UserEditModal = ({ context, id, innerProps }) => {
  const { defaultValues, submit, refetch, isEdit = false } = innerProps;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { control, watch, setValue, formState: { errors } } = useForm({
    resolver: yupResolver(validation),
    context: { isEdit },
    defaultValues: defaultValues || {
      username: '',
      email: '',
      password: '',
      role: 'sales',
      branch: 'AVDL',
      allow_login: true,
      status: 'active'
    }
  });

  const allowLogin = watch('allow_login');

  const handleSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      
      // If editing and password is empty, remove it from the data
      if (isEdit && !data.password) {
        delete data.password;
      }

      await submit(data);
      await refetch();
      context.closeModal(id);
      successToast(isEdit ? 'User updated successfully' : 'User created successfully');
    } catch (error) {
      console.error('Error saving user:', error);
      errorToast(error.response?.data?.message || 'An error occurred while saving the user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const roleOptions = [
    { value: 'admin', label: 'Admin' },
    { value: 'bm', label: 'Branch Manager' },
    { value: 'sales', label: 'Sales' },
    { value: 'rep', label: 'Rep' },
    { value: 'fleet', label: 'Fleet' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'locked', label: 'Locked' }
  ];

  const branchOptions = [
    { value: 'AVDL', label: 'AVDL' },
    { value: 'AVDL2', label: 'AVDL2' },
    { value: 'Finchley', label: 'Finchley' },
    { value: 'ALL', label: 'All Branches' }
  ];

  return (
    <Form control={control} onSubmit={(e) => handleSubmit(e.data)} onError={(e) => console.log(e)}>
      <div className="space-y-4">
        <Grid>
          <Grid.Col span={6}>
            <TextInput
              name="username"
              label="Username"
              placeholder="Enter username"
              control={control}
              required
              error={errors.username?.message}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              name="email"
              label="Email"
              type="email"
              placeholder="Enter email"
              control={control}
              required
              error={errors.email?.message}
            />
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={isEdit ? 12 : 6}>
            <TextInput
              name="password"
              label="Password"
              type="password"
              placeholder={isEdit ? "Leave blank to keep current password" : "Enter password"}
              control={control}
              required={!isEdit}
              error={errors.password?.message}
            />
            {isEdit && (
              <p className="text-xs text-gray-500 mt-1">
                Leave blank to keep the current password
              </p>
            )}
          </Grid.Col>
          {!isEdit && (
            <Grid.Col span={6}>
              <Alert color="blue" className="text-xs">
                Password must be at least 8 characters with one letter, one number, and one special character
              </Alert>
            </Grid.Col>
          )}
        </Grid>

        <Grid>
          <Grid.Col span={6}>
            <NativeSelect
              name="role"
              label="Role"
              data={roleOptions}
              control={control}
              required
              error={errors.role?.message}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <NativeSelect
              name="branch"
              label="Branch"
              data={branchOptions}
              control={control}
              required
              error={errors.branch?.message}
            />
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={6}>
            <NativeSelect
              name="status"
              label="Status"
              data={statusOptions}
              control={control}
              required
              error={errors.status?.message}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <div className="pt-6">
              <Switch
                label="Allow Login"
                checked={allowLogin}
                onChange={(e) => setValue('allow_login', e.currentTarget.checked)}
                error={errors.allow_login?.message}
              />
            </div>
          </Grid.Col>
        </Grid>

        {errors.root && (
          <Alert icon={<XCircleIcon className="w-5 h-5" />} color="red">
            {errors.root.message}
          </Alert>
        )}

        <div className="flex justify-end space-x-3 pt-4">
          <Button
            variant="outline"
            onClick={() => context.closeModal(id)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            loading={isSubmitting}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            {isEdit ? 'Update User' : 'Create User'}
          </Button>
        </div>
      </div>
    </Form>
  );
};

export default UserEditModal;

